import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { FunctionalTrainerBuilder } from "../../functionalTrainerBuilder"
import { buildFrontBottomPulley, buildHalfRackTopPulley, buildTopBackPulley, buildBottomBackPulley, buildFullRackTopPulley } from "./indyPulleys"
import { is80Height, isHalfRack, getDepthInInches, IndyContext, is43Depth } from "./indyHelpers"
import { buildSideMeshes } from "./indyWeightStack"

// IndyBuilder — sub-assemblies split across indyHelpers, indyOffsets, indyWeightStack, indyCarriage, indyPulleys
export class IndyBuilder extends FunctionalTrainerBuilder {
  
  // MARK: Abstract Method Implementations
  protected getModelPrefix(): string {
    return is80Height(this.config) ? 'INDY_80' : 'INDY'
  }
  
  protected addResistanceSystem(): void {
    // No-op: Indy's weight stack is on the sides, not the back
  }
  
  protected buildSideMeshes(materials: MaterialInfo, left: boolean): PartDefinition[] {
    return buildSideMeshes(this.createContext(materials, left))
  }
  
  protected buildFrontBottomPulley(materials: MaterialInfo, left: boolean): PartDefinition {
    const trolleyClip = this.buildTrolleyClip(left)
    
    return buildFrontBottomPulley(this.createContext(materials, left), trolleyClip)
  }
  
  protected buildHalfRackTopPulley(materials: MaterialInfo, left: boolean): PartDefinition {
    return buildHalfRackTopPulley(this.createContext(materials, left))
  }
  
  // MARK: Context Factory
  private createContext(materials: MaterialInfo, left: boolean): IndyContext {
    // Bundles the config (inherited from the parent class), material, and left
    return { config: this.config, materials, left }
  }
  
  private createFTMaterials(): MaterialInfo {
    const baseMaterials = this.materialBuilder.createMainMaterial()
    const materials = JSON.parse(JSON.stringify(baseMaterials)) as MaterialInfo
    
    if (this.config.materials?.ft) {
      Object.assign(materials, this.config.materials.ft)
    }
    
    return materials
  }
  
  // MARK: Public API (for Cave 2.0)
  public attachToRack(
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition,
    left: boolean
  ): void {
    const materials = this.createFTMaterials()
    this.addSideComponents(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright, left)
  }

  // Returns parts for external attachment (Cave pattern)
  public createCaveAssembly(left: boolean) {
    const materials = this.createFTMaterials()
    const context = this.createContext(materials, left)

    const assembly: {
      topPulley: PartDefinition
      bottomPulley: PartDefinition
      sideMeshes: PartDefinition[]
      topBackPulley?: PartDefinition
      bottomBackPulley?: PartDefinition
    } = {
      topPulley    : this.buildHalfRackTopPulley(materials, left),
      bottomPulley : this.buildFrontBottomPulley(materials, left),
      sideMeshes   : this.buildSideMeshes(materials, left),
    }

    // Depth 4 (43") requires additional back pulleys to route the cable around the rear
    if (is43Depth(this.config)) {
      assembly.topBackPulley = buildTopBackPulley(context)
      assembly.bottomBackPulley = buildBottomBackPulley(context)
    }

    return assembly
  }
  
  // MARK: Side Assembly
  protected addLeftSide(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition
  ): void {
    if (!this.config.leftSide) return
    this.addSideComponents(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright, true)
  }

  protected addRightSide(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition
  ): void {
    if (!this.config.rightSide) return
    this.addSideComponents(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright, false)
  }
  
  protected addSideComponents(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition,
    left: boolean
  ): void {
    const sideMeshes = this.buildSideMeshes(materials, left)
    const frontBottomPulley = this.buildFrontBottomPulley(materials, left)
  
    if (isHalfRack(this.config)) {
      this.attachToHalfRack(sideMeshes, frontBottomPulley, topCrossmember, bottomCrossmember, left)
    } else {
      this.attachToFullRack(sideMeshes, frontBottomPulley, topCrossmember, bottomCrossmember, materials, left)
    }
  
    if (this.config.rackRoller && left) {
      this.addRackRoller(isHalfRack(this.config) ? frontUpright : bottomCrossmember)
    }
  }
  
  // MARK: Rack Attachment
  protected attachToHalfRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    left: boolean
  ): void {
    const context = this.createContext(this.createFTMaterials(), left)
    const depth = getDepthInInches(this.config)
    
    if (depth === '43') {
      topCrossmember.slots.side_1 = buildTopBackPulley(context)
      bottomCrossmember.slots['4'] = [...sideMeshes, frontBottomPulley, buildBottomBackPulley(context)]
    } else if (depth === '24') {
      bottomCrossmember.slots.side_3 = sideMeshes
      bottomCrossmember.slots['4'] = frontBottomPulley
    } else {
      bottomCrossmember.slots['4'] = [...sideMeshes, frontBottomPulley]
    }

    topCrossmember.slots.ftx = this.buildHalfRackTopPulley(context.materials, left)
  }
  
  protected attachToFullRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    materials: MaterialInfo,
    left: boolean
  ): void {
    const context = this.createContext(materials, left)
    const topStorage = (bottomCrossmember.slots.back as PartDefinition)?.slots['crossmember-top'] as PartDefinition
    const bottomStorage = (bottomCrossmember.slots.back as PartDefinition)?.slots['crossmember-bottom'] as PartDefinition
    
    // Front attachments
    topCrossmember.slots.ftx = buildFullRackTopPulley(context)
    bottomCrossmember.slots['4'] = frontBottomPulley
    
    // Back storage attachments
    topStorage.slots.side_2 = buildTopBackPulley(context)
    bottomStorage.slots.side_3 = sideMeshes
    bottomStorage.slots['4'] = buildBottomBackPulley(context)
  }
  
  // MARK: J-Cup Adjustment (Indy HR specific)
  protected adjustJCupOffsets(baseRack: PartDefinition): void {
    if (!isHalfRack(this.config) || !this.config.jcup) return
  
    const jCupOffset = is80Height(this.config) ? { y: -14 } : { y: -8 }
  
    const leftJcup = baseRack.slots['jcup-high']
    if (leftJcup && !Array.isArray(leftJcup)) {
      leftJcup.offset = jCupOffset
    }
  
    const rightUpright = baseRack.slots['other-side'] as PartDefinition
    if (rightUpright) {
      const rightJcup = rightUpright.slots['jcup-high']
      if (rightJcup && !Array.isArray(rightJcup)) {
        rightJcup.offset = jCupOffset
      }
    }
  }
}